import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import {
  useBankStore,
  useCampaignStore,
  useCreateChartOfAccount,
  useFinanceSettingsStore,
  useRevenueStore
} from "features/finances";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import * as yup from "yup";
import { EthiopianDateField } from "../EthiopianDateField";
import { ValidationError } from "../ValidationError";
import { BaseModal } from "./BaseModal";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  font-family: inherit;
  background-color: transparent;
`;

const chartOfAccountSchema = yup.object({
  accountNumber: yup.string().required("Account Number is required"),
  accountName: yup.string().required("Account Name is required"),
  accountType: yup.string().required("Account Type is required"),
  description: yup.string().required("Description is required"),
  beginningBalance: yup
    .number()
    .typeError("Beginning Balance must be a number")
    .required("Beginning Balance is required"),
  beginningBalanceDate: yup
    .string()
    .required("Beginning Balance Date is required")
});

export function CreateChartOfAccountModal() {
  const createChartOfAccountOpen = useFinanceSettingsStore(
    state => state.createChartOfAccountOpen
  );
  const closeCreateChartOfAccountModal = useFinanceSettingsStore(
    state => state.closeCreateChartOfAccountModal
  );
  const openCreateBankAccountModal = useBankStore(
    state => state.openCreateBankAccountModal
  );
  const openRecordRevenueModal = useRevenueStore(
    state => state.openRecordRevenueModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const createChartOfAccountMutation = useCreateChartOfAccount();
  const returnToBankAccount = useFinanceSettingsStore(
    state => state.returnToBankAccount
  );
  const returnToRevenue = useFinanceSettingsStore(
    state => state.returnToRevenue
  );
  const returnToCampaign = useFinanceSettingsStore(
    state => state.returnToCampaign
  );
  const openCampaignModal = useCampaignStore(state => state.openCampaignModal);

  const accountTypes = ["ASSET", "LIABILITY", "EQUITY", "INCOME", "EXPENSE"];

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors },
    control
  } = useForm({
    defaultValues: {
      accountNumber: "",
      accountName: "",
      accountType: "",
      description: "",
      beginningBalance: 0,
      beginningBalanceDate: ""
    },
    resolver: yupResolver(chartOfAccountSchema)
  });

  const beginningBalanceDate = watch("beginningBalanceDate");

  const onSubmit = data => {
    createChartOfAccountMutation.mutate(
      {
        churchId: selectedChurch?.id,
        chartOfAccountData: {
          account_number: data.accountNumber,
          account_name: data.accountName,
          account_type: data.accountType,
          description: data.description,
          beginning_balance: data.beginningBalance,
          beginning_balance_date: data.beginningBalanceDate
        }
      },
      {
        onSuccess: () => {
          reset();
          if (returnToBankAccount) {
            closeCreateChartOfAccountModal();
            openCreateBankAccountModal();
          } else if (returnToRevenue) {
            closeCreateChartOfAccountModal();
            openRecordRevenueModal();
          } else if (returnToCampaign) {
            closeCreateChartOfAccountModal();
            openCampaignModal();
          } else {
            closeCreateChartOfAccountModal();
          }
        },
        onError: response => {
          setError("accountNumber", {
            type: "custom",
            message: response.errors?.account_number
          });
        }
      }
    );
  };

  return (
    <BaseModal
      title="Create Chart of Account"
      isOpen={createChartOfAccountOpen}
      onClose={closeCreateChartOfAccountModal}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            flavor="white"
            size="small"
            onClick={closeCreateChartOfAccountModal}
            disabled={createChartOfAccountMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleSubmit(onSubmit)}
            disabled={createChartOfAccountMutation.isLoading}
          >
            Create
          </Button>
        </Stack>
      }
    >
      <Field>
        <Label htmlFor="accountNumber">Account ID</Label>
        <Input id="accountNumber" {...register("accountNumber")} />
        <ErrorMessage
          errors={errors}
          name="accountNumber"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="accountName">Account Name</Label>
        <Input id="accountName" {...register("accountName")} />
        <ErrorMessage
          errors={errors}
          name="accountName"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="accountType">Account Type</Label>
        <Select id="accountType" {...register("accountType")}>
          <option value="" disabled>
            Select account type
          </option>
          {accountTypes.map(accountType => (
            <option key={accountType} value={accountType}>
              {accountType}
            </option>
          ))}
        </Select>
        <ErrorMessage
          errors={errors}
          name="accountType"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="description">Description</Label>
        <Input id="description" {...register("description")} />
        <ErrorMessage
          errors={errors}
          name="description"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="beginningBalance">Beginning Balance</Label>
        <Controller
          name="beginningBalance"
          control={control}
          render={({ field }) => (
            <NumericFormat
              customInput={Input}
              id="beginningBalance"
              thousandSeparator
              allowNegative={false}
              onValueChange={values => {
                field.onChange(values.floatValue);
              }}
              value={field.value}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name="beginningBalance"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="beginningBalanceDate">Beginning Balance Date</Label>
        <EthiopianDateField
          size="small"
          value={beginningBalanceDate}
          onChange={e => setValue("beginningBalanceDate", e.target.value)}
        />
        <ErrorMessage
          errors={errors}
          name="beginningBalanceDate"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>

      <MutationError
        error={createChartOfAccountMutation.error}
        sx={{ mt: 2 }}
      />
    </BaseModal>
  );
}
